import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";

import { AppShell } from "@/app/layout/AppShell";
import { ProfilePage } from "@/pages/profile/ProfilePage";
import { searchParamSchema, wyckoffSearchParamSchema } from "@/schemas/api/market";
import { MarketTerminalPage } from "@/pages/market/MarketTerminalPage";
import { WyckoffTerminalPage } from "@/pages/wyckoff/WyckoffTerminalPage";
import { QuantWorkbenchPage } from "@/pages/quant/QuantWorkbenchPage";
import { OpsDashboardPage } from "@/pages/ops/OpsDashboardPage";
import { OpsWorkbenchPage } from "@/pages/ops/OpsWorkbenchPage";
import { NewsWorkbenchPage } from "@/pages/news/NewsWorkbenchPage";
import { DataFlowPage } from "@/pages/dataflow/DataFlowPage";

const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

const stockRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/stock",
  validateSearch: (search) => {
    const parsed = searchParamSchema.safeParse({
      ...search,
      watchlistId: search.watchlistId ? Number(search.watchlistId) : null,
      indicators: typeof search.indicators === "string" ? String(search.indicators).split(",").filter(Boolean) : search.indicators,
    });
    return parsed.success ? parsed.data : searchParamSchema.parse({});
  },
  component: MarketTerminalPage,
});

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/profile",
  component: ProfilePage,
});

const wyckoffRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/wyckoff",
  validateSearch: (search) => {
    const parsed = wyckoffSearchParamSchema.safeParse({
      ...search,
      watchlistId: search.watchlistId ? Number(search.watchlistId) : null,
      limit: search.limit ? Number(search.limit) : undefined,
    });
    return parsed.success ? parsed.data : wyckoffSearchParamSchema.parse({});
  },
  component: WyckoffTerminalPage,
});

const quantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quant",
  component: QuantWorkbenchPage,
});

const opsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ops",
  component: OpsDashboardPage,
});

const opsDatasyncRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ops/datasync",
  component: OpsWorkbenchPage,
});

const newsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/news",
  component: NewsWorkbenchPage,
});

const dataflowRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dataflow",
  component: DataFlowPage,
});

const routeTree = rootRoute.addChildren([
  stockRoute,
  profileRoute,
  wyckoffRoute,
  quantRoute,
  opsRoute,
  opsDatasyncRoute,
  newsRoute,
  dataflowRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultStructuralSharing: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
